import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import HelpTooltip from '@/components/help/HelpTooltip';
import { Users, AlertTriangle, CheckCircle2, Trash2, Upload, Image as ImageIcon } from 'lucide-react';

/**
 * Con qué número le avisamos al EQUIPO (no a los clientes).
 *
 * EL PROBLEMA QUE ESTA PANTALLA RESUELVE
 * ======================================
 *
 * Una misma persona puede ser vendedor en un negocio y cliente en otro. Si las
 * dos cosas le llegan desde el mismo número, para WhatsApp es UNA sola
 * conversación y la información se le mezcla.
 *
 * La regla: el número que le habla a tu equipo es el de quien te vendió el
 * sistema; el que le habla a tus clientes es el del negocio.
 *
 * POR QUÉ CADA SOCIO TIENE EL SUYO
 * ================================
 *
 * Si a los vendedores de un socio de marca blanca les llegara el aviso desde
 * CavernicolApp, se rompería la marca blanca que le vendimos. Por eso hay una
 * fila por socio y no una sola para todos.
 */

const PROVEEDORES = {
  gupshup: 'Gupshup',
  twilio_whatsapp: 'Twilio WhatsApp',
  meta_whatsapp: 'Meta / WhatsApp Business',
};

// Lo que hay que pedirle a cada proveedor. Sale de lo que ya usa cada adaptador
// de envío; poner campos genéricos («api_key») dejaría credenciales que el
// adaptador no sabe leer y el envío fallaría sin decir por qué.
const CAMPOS = {
  gupshup: [
    { k: 'app_key', label: 'App Key', ayuda: 'La App Key de la app de Gupshup con el WhatsApp ya aprobado por Meta.' },
    { k: 'source', label: 'Número de origen', ayuda: 'El número con el que sale el mensaje, sin espacios ni guiones. Ej. 5215512345678' },
    { k: 'app_name', label: 'Nombre de la app', ayuda: 'El nombre exacto de la app en Gupshup.' },
  ],
  twilio_whatsapp: [
    { k: 'account_sid', label: 'Account SID', ayuda: 'Empieza con "AC...". Está en Twilio Console → Account Info.' },
    { k: 'auth_token', label: 'Auth Token', ayuda: 'Junto al Account SID en Twilio Console.' },
    { k: 'from_number', label: 'Número de origen', ayuda: 'El número de WhatsApp de Twilio. Ej. +5215512345678' },
  ],
  meta_whatsapp: [
    { k: 'access_token', label: 'Token de acceso', ayuda: 'El token permanente de la app de Meta con permiso de WhatsApp.' },
    { k: 'phone_number_id', label: 'ID del número', ayuda: 'El "Phone number ID" que da Meta, no el número en sí.' },
  ],
};

// Los fondos que se ofrecen. Es una lista corta a propósito: el fondo solo
// existe para que el logo se vea, no para decorar, y una paleta abierta invita
// a elegir un color que le pelee al logotipo.
const FONDOS = [
  { hex: '#FFFFFF', nombre: 'Blanco', clase: 'bg-white border-slate-300' },
  { hex: '#0F172A', nombre: 'Negro', clase: 'bg-slate-900 border-slate-900' },
  { hex: '#F1F5F9', nombre: 'Gris claro', clase: 'bg-slate-100 border-slate-300' },
];

/**
 * El logo que va ARRIBA del aviso, en el encabezado de la plantilla.
 *
 * Va suelto y no dentro de «Configurar» a propósito: el dueño tiene su logo hoy
 * y el número del socio puede tardar días en llegar. Si el logo viviera detrás
 * del formulario de credenciales, no podría poner lo que sí tiene.
 */
function LogoDelRemitente({ partnerId, actual, onCambio }) {
  const [subiendo, setSubiendo] = useState(false);
  const [fondo, setFondo] = useState(actual?.fondo_del_logo || '#FFFFFF');
  const [conversion, setConversion] = useState(null);

  const subir = async (archivo) => {
    if (!archivo) return;
    setSubiendo(true);
    setConversion(null);
    try {
      const cuerpo = new FormData();
      cuerpo.append('archivo', archivo);
      cuerpo.append('fondo', fondo);
      if (partnerId) cuerpo.append('partner_id', partnerId);
      // El Content-Type va explícito: el cliente `api` pone json en TODAS las
      // peticiones, y con json el backend no ve el archivo y responde «falta el
      // campo», culpando al archivo del usuario. Ya pasó dos veces.
      const { data } = await api.post(
        '/admin/infrastructure/remitente-avisos-internos/logo', cuerpo,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      setConversion(data.conversion || null);
      toast.success('Logo guardado.');
      onCambio();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubiendo(false);
    }
  };

  const quitar = async () => {
    setSubiendo(true);
    try {
      await api.delete('/admin/infrastructure/remitente-avisos-internos/logo',
                       { params: partnerId ? { partner_id: partnerId } : {} });
      setConversion(null);
      toast.success('Logo quitado. Los avisos saldrán sin encabezado.');
      onCambio();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubiendo(false);
    }
  };

  return (
    <div className="mt-4 border-t border-slate-200 dark:border-slate-700 pt-4">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="shrink-0">
          {actual?.logo_url ? (
            <img src={actual.logo_url} alt="Logo del encabezado"
                 className="h-20 w-20 rounded-md border border-slate-200 dark:border-slate-700 object-contain" />
          ) : (
            <div className="h-20 w-20 rounded-md border border-dashed border-slate-300 dark:border-slate-600
                            flex items-center justify-center text-slate-400">
              <ImageIcon className="h-7 w-7" />
            </div>
          )}
        </div>

        <div className="flex-1 min-w-[240px] space-y-2">
          <Label className="flex items-center gap-1.5">
            Logo del encabezado
            <HelpTooltip text="Es la imagen que verá el vendedor arriba del aviso, antes del texto. Sube el logo que ya tienes: si es horizontal o tiene fondo transparente lo acomodamos nosotros en un cuadrado, que es lo que WhatsApp necesita." />
          </Label>

          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-slate-600 dark:text-slate-400">Fondo:</span>
            {FONDOS.map((f) => (
              <button
                key={f.hex}
                type="button"
                onClick={() => setFondo(f.hex)}
                title={f.nombre}
                aria-label={`Fondo ${f.nombre}`}
                className={`h-6 w-6 rounded-full border-2 ${f.clase} ${
                  fondo === f.hex ? 'ring-2 ring-offset-1 ring-slate-900 dark:ring-slate-200' : ''}`}
              />
            ))}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <label className="inline-flex">
              <input type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden"
                     disabled={subiendo}
                     onChange={(e) => { subir(e.target.files?.[0]); e.target.value = ''; }} />
              <span className="inline-flex items-center gap-1.5 rounded-md border border-slate-300
                               dark:border-slate-600 px-3 py-1.5 text-sm cursor-pointer
                               hover:bg-slate-50 dark:hover:bg-slate-800">
                <Upload className="h-4 w-4" />
                {subiendo ? 'Subiendo…' : actual?.tiene_logo ? 'Cambiar logo' : 'Subir logo'}
              </span>
            </label>
            {actual?.tiene_logo && (
              <Button variant="ghost" size="sm" onClick={quitar} disabled={subiendo}>
                <Trash2 className="h-4 w-4 text-red-600" />
              </Button>
            )}
          </div>

          <p className="text-xs text-slate-500 dark:text-slate-400">
            PNG, JPG o WEBP, hasta 3 MB. Los archivos SVG no se aceptan por seguridad.
          </p>

          {/* Lo que se le hizo a su archivo se le dice. Convertir en silencio es
              la otra forma de mentir: el dueño sube un logo horizontal, ve otra
              cosa, y no entiende por qué. */}
          {conversion && (
            <div className="rounded-md bg-slate-50 dark:bg-slate-800 p-3 text-xs
                            text-slate-700 dark:text-slate-300 space-y-1">
              <p className="font-medium">Qué hicimos con tu archivo:</p>
              <p>
                Llegó de {conversion.medidas_originales} píxeles
                {conversion.era_cuadrada
                  ? ' y ya era cuadrado.'
                  : ' (no era cuadrado), así que lo centramos en un cuadrado sin recortarlo ni deformarlo.'}
              </p>
              {conversion.tenia_transparencia && (
                <p>
                  Tenía fondo transparente. Le pusimos el fondo {conversion.fondo_aplicado}:
                  sin eso, el mismo logo se vería sobre blanco en un teléfono y sobre
                  negro en otro, según el tema de quien lo recibe.
                </p>
              )}
              <p>Así es exactamente como lo va a ver tu vendedor.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Formulario({ titulo, subtitulo, actual, partnerId, onGuardado }) {
  const [proveedor, setProveedor] = useState(actual?.proveedor || '');
  const [valores, setValores] = useState({});
  const [identidad, setIdentidad] = useState(actual?.identidad || '');
  const [guardando, setGuardando] = useState(false);
  const [abierto, setAbierto] = useState(false);

  useEffect(() => {
    setProveedor(actual?.proveedor || '');
    setIdentidad(actual?.identidad || '');
    setValores({});
  }, [actual]);

  const guardar = async () => {
    if (!proveedor) { toast.error('Elige con qué proveedor va a mandar.'); return; }
    const faltan = (CAMPOS[proveedor] || []).filter((c) => !((valores[c.k] || '').trim()));
    if (faltan.length) {
      toast.error(`Falta ${faltan.map((f) => f.label).join(', ')}.`);
      return;
    }
    setGuardando(true);
    try {
      await api.put('/admin/infrastructure/remitente-avisos-internos', {
        proveedor,
        credentials: Object.fromEntries(
          (CAMPOS[proveedor] || []).map((c) => [c.k, (valores[c.k] || '').trim()])),
        identidad: identidad.trim() || null,
        partner_id: partnerId || null,
      });
      toast.success('Guardado. Los avisos al equipo ya saldrán de este número.');
      setValores({});
      setAbierto(false);
      onGuardado();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardando(false);
    }
  };

  const quitar = async () => {
    if (!actual?.id) return;
    setGuardando(true);
    try {
      await api.delete(`/admin/infrastructure/remitente-avisos-internos/${actual.id}`);
      toast.success('Quitado. Este nivel vuelve a usar el número de arriba.');
      onGuardado();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="font-medium text-slate-900 dark:text-slate-100">{titulo}</p>
          <p className="text-sm text-slate-600 dark:text-slate-400">{subtitulo}</p>
        </div>
        <div className="flex items-center gap-2">
          {actual ? (
            <span className="inline-flex items-center gap-1.5 text-sm text-emerald-700 dark:text-emerald-400">
              <CheckCircle2 className="h-4 w-4" />
              {PROVEEDORES[actual.proveedor] || actual.proveedor}
              {actual.identidad ? ` · ${actual.identidad}` : ''}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-sm text-amber-700 dark:text-amber-400">
              <AlertTriangle className="h-4 w-4" /> Sin configurar
            </span>
          )}
          <Button variant="outline" size="sm" onClick={() => setAbierto((v) => !v)}>
            {abierto ? 'Cerrar' : actual ? 'Cambiar' : 'Configurar'}
          </Button>
          {actual && (
            <Button variant="ghost" size="sm" onClick={quitar} disabled={guardando}
                    title="Quitar este remitente">
              <Trash2 className="h-4 w-4 text-red-600" />
            </Button>
          )}
        </div>
      </div>

      {abierto && (
        <div className="mt-4 space-y-3 border-t border-slate-200 dark:border-slate-700 pt-4">
          <div>
            <Label>Con qué proveedor manda</Label>
            <Select value={proveedor} onValueChange={(v) => { setProveedor(v); setValores({}); }}>
              <SelectTrigger><SelectValue placeholder="Elige un proveedor" /></SelectTrigger>
              <SelectContent>
                {Object.entries(PROVEEDORES).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {(CAMPOS[proveedor] || []).map((c) => (
            <div key={c.k}>
              <Label className="flex items-center gap-1.5">
                {c.label}
                <HelpTooltip text={c.ayuda} />
              </Label>
              <Input
                type="password"
                autoComplete="new-password"
                value={valores[c.k] || ''}
                onChange={(e) => setValores((p) => ({ ...p, [c.k]: e.target.value }))}
                placeholder={actual ? 'Escribe el valor nuevo' : ''}
              />
            </div>
          ))}

          {proveedor && (
            <div>
              <Label className="flex items-center gap-1.5">
                Cómo lo quieres ver aquí (opcional)
                <HelpTooltip text="Solo es una etiqueta para que tú lo reconozcas en esta pantalla, por ejemplo «+52 55 1234 5678». No cambia nada del envío." />
              </Label>
              <Input value={identidad} onChange={(e) => setIdentidad(e.target.value)}
                     placeholder="+52 55 1234 5678" />
            </div>
          )}

          {proveedor && (
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Usa un número <strong>distinto</strong> al que los negocios usan con sus
              clientes. Si repites el mismo, vuelve a pasar justo lo que esto viene a
              arreglar: las dos conversaciones caen en el mismo hilo.
            </p>
          )}

          <Button onClick={guardar} disabled={guardando || !proveedor}>
            {guardando ? 'Guardando…' : 'Guardar'}
          </Button>
        </div>
      )}

      {/* El logo va SIEMPRE visible, no dentro de «Configurar»: no depende de
          tener credenciales y es lo que el dueño puede resolver hoy mismo. */}
      <LogoDelRemitente partnerId={partnerId} actual={actual} onCambio={onGuardado} />
    </div>
  );
}

export default function RemitenteAvisosSection() {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async () => {
    try {
      const r = await api.get('/admin/infrastructure/remitente-avisos-internos');
      setDatos(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
      <div className="h-1 bg-emerald-500" />
      <div className="p-5">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-emerald-600" />
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            Avisos a vendedores y administradores
          </h3>
          <HelpTooltip text="Aquí eliges con qué número de WhatsApp se le avisa al EQUIPO de cada negocio («te asignaron un lead», «tienes una tarea»). A los clientes se les sigue escribiendo desde el número del propio negocio — eso no se toca aquí." />
        </div>

        <div className="mt-3 rounded-lg bg-slate-50 dark:bg-slate-800 p-3 text-sm text-slate-700 dark:text-slate-300">
          <p>
            <strong>La regla:</strong> el número que le habla a tu equipo es el de quien
            te vendió el sistema. El que le habla a tus clientes es el del negocio.
          </p>
          <p className="mt-2">
            Esto existe porque una misma persona puede ser vendedor en un negocio y
            cliente en otro. Si las dos cosas le llegan del mismo número, WhatsApp las
            junta en una sola conversación y se le mezcla la información.
          </p>
          <p className="mt-2 text-slate-600 dark:text-slate-400">
            <strong>Mientras no configures nada aquí</strong>, todo sigue funcionando
            exactamente como hoy: el aviso sale del número del propio negocio.
          </p>
        </div>

        <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-3 text-sm text-amber-900 dark:text-amber-200">
          <p className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              <strong>Hasta dónde llega hoy:</strong> esto aplica cuando la conversación
              con el vendedor está abierta (él escribió en las últimas 24 horas). Cuando
              está cerrada, el aviso sale por plantilla y esa <strong>todavía</strong> se
              manda desde el número del negocio, porque Meta aprueba las plantillas por
              cuenta y aún no hay ninguna dada de alta a nivel plataforma.
            </span>
          </p>
        </div>

        {cargando ? (
          <p className="mt-4 text-sm text-slate-500">Cargando…</p>
        ) : (
          <div className="mt-4 space-y-3">
            <Formulario
              titulo="CavernicolApp (plataforma)"
              subtitulo="Se usa con los negocios que te compraron directo a ti."
              actual={datos?.plataforma}
              partnerId={null}
              onGuardado={cargar}
            />

            {(datos?.socios || []).length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400 px-1">
                Todavía no hay socios dados de alta. Cuando los haya, cada uno aparecerá
                aquí para ponerle su propio número.
              </p>
            ) : (
              (datos?.socios || []).map((s) => (
                <Formulario
                  key={s.partner_id}
                  titulo={s.nombre}
                  subtitulo="Se usa con los negocios de este socio, para que el aviso salga con SU marca y no con la nuestra."
                  actual={s.remitente}
                  partnerId={s.partner_id}
                  onGuardado={cargar}
                />
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
